import { useState, useEffect, useContext, useMemo } from "react";
import marker from "leaflet/src/images/marker.svg";
import marker2 from "../assets/markers/marker2.svg";
import marker3 from "../assets/markers/marker3.svg";
import marker4 from "../assets/markers/marker4.svg";
import L, { Icon } from "leaflet";
import ContextStates from "../context/ContextStates";
import { MapContainer, TileLayer, Popup, Marker, useMap } from "react-leaflet";
import axios from "axios";
import { Url } from "./Url";
import Filtros from "./Filtros";
import { useNavigate } from "react-router-dom";
import "leaflet/dist/leaflet.css";

// ====== ICONOS ======
const myIcon = new Icon({ iconUrl: marker, iconSize: [32, 32] }); // pedido pendiente
const myIcon2 = new Icon({ iconUrl: marker2, iconSize: [32, 32] }); // rechazado
const myIcon3 = new Icon({ iconUrl: marker3, iconSize: [32, 32] }); // sin visita
const myIcon4 = new Icon({ iconUrl: marker4, iconSize: [32, 32] }); // entregado / variación

// ====== TIPOS ======
const tiposPedidos = [
    { id: 0, descripcion: "Pedidos Nuevos" },
    { id: 1, descripcion: "Pedidos Entregados" },
    { id: 2, descripcion: "Pedidos No aceptados" },
];

// ====== HELPER: elegir icono por marker code ======
function getIconByMarkerCode(code) {
    const c = Number(code);
    if (c === 0) return myIcon3; // sin visita (gris)
    if (c === 1) return myIcon;  // con pedido (azul)
    if (c === 2) return myIcon4; // entregado (verde/naranja)
    return myIcon2; // default/no compra (rojo)
}

// ====== COMPONENTE: Recentrar cuando cambia "position" ======
function RecenterOnPosition({ position, zoom = 16, animate = true }) {
    const map = useMap();
    useEffect(() => {
        if (!position) return;
        map.setView(position, zoom, { animate });
        // Alternativa:
        // map.flyTo(position, zoom);
    }, [map, position, zoom, animate]);
    return null;
}

// ====== COMPONENTE: Ajustar a todos los marcadores cuando cambian (fitBounds) ======
function FitBoundsToMarkers({ pedidos, padding = 0.2 }) {
    const map = useMap();
    useEffect(() => {
        if (!pedidos || !pedidos.length) return;

        const coords = pedidos
            .filter(p => p.latitud && p.longitud)
            .map(p => [parseFloat(p.latitud), parseFloat(p.longitud)]);

        if (!coords.length) return;

        if (coords.length === 1) {
            // Un solo punto: centramos con un zoom razonable
            map.setView(coords[0], 16, { animate: true });
        } else {
            const bounds = L.latLngBounds(coords);
            map.fitBounds(bounds.pad(padding), { animate: true });
        }
    }, [map, pedidos, padding]);

    return null;
}

// ====== MAPA ======
const Map = () => {
    const [position, setPosition] = useState([-26.833018562210178, -65.20875863504794]); // fallback
    const navigate = useNavigate();

    const {
        setPedidoSelected,
        tipoPedido,
        setTipoPedido,
        setZonas,
        zona,
        fechaInicio,
        fechaFin,
        usuario,
        pedidos,
        setPedidos,
        setClienteSelected,
    } = useContext(ContextStates);

    // Cargar zonas una vez
    useEffect(() => {
        const consultaZonas = async () => {
            const formData = new FormData();
            formData.append("id", 1);
            formData.append("activo", 1);
            try {
                const response = await axios.post(Url + "api/listZonas.php", formData);
                setZonas(response.data.results);
            } catch (e) {
                console.log(e);
            }
        };
        consultaZonas();
    }, [setZonas]);

    // Consultar pedidos cada vez que cambian filtros
    useEffect(() => {
        const consultaPedidos = async () => {
            if (!zona?.id && !usuario?.id && !fechaInicio && !fechaFin) return;
            const formData = new FormData();
            formData.append("zona", zona?.id || 0);
            formData.append("fechaInicio", fechaInicio || "");
            formData.append("fechaFin", fechaFin || "");
            formData.append("tipoPedido", tipoPedido);
            formData.append("usuario", usuario?.id || 0);

            try {
                const { data } = await axios.post(Url + "api/listadoPedidos.php", formData);
                const arr = Array.isArray(data?.results) ? data.results : [];
                setPedidos(arr);

                // Si querés centrar inicialmente al primer resultado (además del fitBounds):
                if (arr.length > 0) {
                    const lat = parseFloat(arr[0].latitud);
                    const lon = parseFloat(arr[0].longitud);
                    if (Number.isFinite(lat) && Number.isFinite(lon)) {
                        setPosition([lat, lon]); // esto se usa para RecenterOnPosition si querés
                    }
                }
            } catch (e) {
                console.log(e);
            }
        };
        consultaPedidos();
    }, [zona, fechaInicio, fechaFin, tipoPedido, usuario, setPedidos]);

    const conteoMarcadores = useMemo(() => {
        const counts = { sinVisita: 0, conPedido: 0, entregado: 0, noCompro: 0 };
        (pedidos || []).forEach(p => {
            const m = Number(p.marker);
            if (m === 0) counts.sinVisita++;
            else if (m === 1) counts.conPedido++;
            else if (m === 2) counts.entregado++;
            else counts.noCompro++;
        });
        return counts;
    }, [pedidos]);

    const handlePedido = (item) => {
        setPedidoSelected(item);
        navigate("/pedido", { replace: true });
    };

    const setPedido = (item) => {
        setClienteSelected(item);
        navigate("/nuevopedido", { replace: true });
    };

    return (
        <div className="mt-1 mb-10 w-full z-0">
            {/* Filtros */}
            <div className="bg-blue-100 rounded-md p-3 mb-5">
                <Filtros />

                {tiposPedidos.map((item) => (
                    <label key={item.id} className="flex items-center gap-2 my-1">
                        <input
                            type="radio"
                            name="tipoPedido"
                            value={item.id}
                            checked={tipoPedido === item.id}
                            onChange={() => setTipoPedido(item.id)}
                            className="mr-2"
                        />
                        {item.descripcion}
                    </label>
                ))}
            </div>

            {/* Referencias de colores */}
            <div className="rounded-md p-5 mb-5">
                <h3 className="text-center text-2xl text-gray-700 uppercase">Referencias de colores</h3>
                <div className="px-6 md:px-20 flex flex-wrap justify-center gap-x-10 gap-y-4 mt-4">
                    <label className="flex items-center border p-2 rounded-lg shadow-sm bg-white">
                        <img className="h-8 md:h-10" src={marker3} alt="sin visita" />
                        <span className="ml-2 font-medium">Sin visita: <strong>{conteoMarcadores.sinVisita}</strong></span>
                    </label>
                    <label className="flex items-center border p-2 rounded-lg shadow-sm bg-white">
                        <img className="h-8 md:h-10" src={marker} alt="con pedido" />
                        <span className="ml-2 font-medium">Con Pedido: <strong>{conteoMarcadores.conPedido}</strong></span>
                    </label>
                    <label className="flex items-center border p-2 rounded-lg shadow-sm bg-white">
                        <img className="h-8 md:h-10" src={marker4} alt="entregado" />
                        <span className="ml-2 font-medium">Entregado: <strong>{conteoMarcadores.entregado}</strong></span>
                    </label>
                    <label className="flex items-center border p-2 rounded-lg shadow-sm bg-white">
                        <img className="h-8 md:h-10" src={marker2} alt="no compró" />
                        <span className="ml-2 font-medium">No compró: <strong>{conteoMarcadores.noCompro}</strong></span>
                    </label>
                </div>
            </div>

            {/* MAPA */}
            <MapContainer
                center={position}
                zoom={18}
                scrollWheelZoom
                className="mx-auto h-full z-0"
                style={{ maxWidth: "80vw", height: 800 }}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {/* Ajusta automáticamente el mapa a TODOS los marcadores cuando cambian */}
                <FitBoundsToMarkers pedidos={pedidos} />

                {/* Si preferís, también podes recentrar “manual” cuando cambie position */}
                <RecenterOnPosition position={position} />

                {pedidos?.map((item, index) => {
                    const lat = parseFloat(item.latitud);
                    const lon = parseFloat(item.longitud);
                    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

                    return (
                        <Marker
                            key={index}
                            position={[lat, lon]}
                            icon={getIconByMarkerCode(item.marker)}
                            eventHandlers={{
                                click: () => setPosition([lat, lon]), // recenter suave al tocar
                            }}
                        >
                            <Popup>
                                <strong>Nombre:</strong> {item.cliente}
                                <br />
                                <strong>Domicilio:</strong> {item.domicilio}
                                <br />
                                <strong>Fecha de pedido:</strong> {item.fechapedido}
                                <br />
                                <strong>Hora de pedido:</strong> {item.horapedido}
                                <br />
                                {(item.estado === 0 || item.estado === 7) && (
                                    <button
                                        className="bg-blue-500 text-white p-2 w-full rounded mt-2"
                                        onClick={() => handlePedido(item)}
                                    >
                                        {item.estado === 0 ? "Ver Pedido" : "Ver Motivo"}
                                    </button>
                                )}
                                {item.estado === 0 && (
                                    <button
                                        className="bg-orange-600 text-white p-2 w-full rounded mt-2"
                                        onClick={() => setPedido(item)}
                                    >
                                        Editar Pedido
                                    </button>
                                )}
                            </Popup>
                        </Marker>
                    );
                })}
            </MapContainer>
        </div>
    );
};

export default Map;
